import { useState } from 'react'
import { Modal } from '@/components/ui/modal'
import type { InfraThreshold } from '@/lib/types'

interface ThresholdEditorProps {
  thresholds: InfraThreshold[]
  onSave: (threshold: InfraThreshold) => void
  isSaving: boolean
}

export function ThresholdEditor({
  thresholds,
  onSave,
  isSaving,
}: ThresholdEditorProps): React.JSX.Element {
  const [editing, setEditing] = useState<InfraThreshold | null>(null)

  function handleSubmit(): void {
    if (!editing) return
    onSave(editing)
    setEditing(null)
  }

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-surface-3 bg-surface-0">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-surface-3 bg-surface-1">
            <tr>
              <th className="px-4 py-3 font-medium text-text-secondary">Metric</th>
              <th className="px-4 py-3 font-medium text-text-secondary">Warning</th>
              <th className="px-4 py-3 font-medium text-text-secondary">Critical</th>
              <th className="px-4 py-3 font-medium text-text-secondary">Sustained</th>
              <th className="px-4 py-3 font-medium text-text-secondary">Enabled</th>
              <th className="px-4 py-3 font-medium text-text-secondary" />
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-3">
            {thresholds.map((t) => (
              <tr key={t.metric}>
                <td className="px-4 py-3 font-medium text-text-primary">{t.metric}</td>
                <td className="px-4 py-3 text-warning">{t.warning}%</td>
                <td className="px-4 py-3 text-danger">{t.critical}%</td>
                <td className="px-4 py-3 text-text-secondary">{t.sustained_readings}x</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${t.enabled ? 'bg-green-500' : 'bg-surface-3'}`}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => setEditing({ ...t })}
                    className="text-sm font-medium text-admin-500 hover:text-admin-700"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={`Edit Threshold: ${editing?.metric ?? ''}`}
        size="sm"
      >
        {editing && (
          <div className="space-y-4">
            <label className="block text-sm text-text-secondary">
              Warning (%)
              <input
                type="number"
                min={0}
                max={100}
                value={editing.warning}
                onChange={(e) => setEditing({ ...editing, warning: Number(e.target.value) })}
                className="mt-1 block w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-text-primary focus:border-admin-500 focus:outline-none"
              />
            </label>
            <label className="block text-sm text-text-secondary">
              Critical (%)
              <input
                type="number"
                min={0}
                max={100}
                value={editing.critical}
                onChange={(e) => setEditing({ ...editing, critical: Number(e.target.value) })}
                className="mt-1 block w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-text-primary focus:border-admin-500 focus:outline-none"
              />
            </label>
            <label className="block text-sm text-text-secondary">
              Sustained readings
              <input
                type="number"
                min={1}
                max={30}
                value={editing.sustained_readings}
                onChange={(e) =>
                  setEditing({ ...editing, sustained_readings: Number(e.target.value) })
                }
                className="mt-1 block w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-text-primary focus:border-admin-500 focus:outline-none"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={editing.enabled}
                onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                className="rounded border-surface-3"
              />
              Enabled
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-lg px-4 py-2 text-sm text-text-secondary hover:bg-surface-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="rounded-lg bg-admin-500 px-4 py-2 text-sm font-medium text-white hover:bg-admin-600 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}
